import { useState, useEffect } from 'react';
import { Calendar, X, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { format, addMonths, subMonths, isSameDay, isWithinInterval, startOfDay } from 'date-fns';
import { it } from 'date-fns/locale';

/**
 * CalendarDrawer - Real calendar panel for date selection
 * 
 * Features:
 * - Single date or range selection
 * - State persistence across open/close
 * - Integration with chat context
 * - Visual feedback for selected dates
 */
export const CalendarDrawer = ({ 
  isOpen, 
  onClose, 
  onDateSelect,
  selectedDates = null,  // { start: Date, end: Date } or { start: Date }
  threadContext = {}     // Current conversation context
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectingRange, setSelectingRange] = useState(false);
  const [rangeStart, setRangeStart] = useState(selectedDates?.start || null);
  const [rangeEnd, setRangeEnd] = useState(selectedDates?.end || null);
  const [selectionMode, setSelectionMode] = useState('range'); // 'single' | 'range' | 'flexible'
  
  // Restore state when panel reopens
  useEffect(() => {
    if (isOpen && selectedDates) {
      setRangeStart(selectedDates.start || null);
      setRangeEnd(selectedDates.end || null);
    }
  }, [isOpen, selectedDates]);
  
  // Generate calendar days for current month
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // Start from Monday
    const startPadding = (firstDay.getDay() + 6) % 7;
    const days = [];
    
    // Previous month padding
    for (let i = startPadding - 1; i >= 0; i--) {
      const date = new Date(year, month, -i);
      days.push({ date, isCurrentMonth: false });
    }
    
    // Current month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    
    // Next month padding (fill to 42 cells = 6 weeks)
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      const date = new Date(year, month + 1, i);
      days.push({ date, isCurrentMonth: false });
    }
    
    return days;
  };
  
  const handleDayClick = (date) => {
    if (selectionMode === 'single') {
      setRangeStart(date);
      setRangeEnd(null);
    } else if (selectionMode === 'range') {
      if (!rangeStart || (rangeStart && rangeEnd)) {
        // Start new selection
        setRangeStart(date);
        setRangeEnd(null);
        setSelectingRange(true);
      } else {
        // Complete range
        if (date < rangeStart) {
          setRangeEnd(rangeStart);
          setRangeStart(date);
        } else {
          setRangeEnd(date);
        }
        setSelectingRange(false);
      }
    }
  };
  
  const isDateSelected = (date) => {
    if (!rangeStart) return false;
    
    const dayStart = startOfDay(date);
    const selStart = startOfDay(rangeStart);
    
    if (!rangeEnd) {
      return isSameDay(dayStart, selStart);
    }
    
    const selEnd = startOfDay(rangeEnd);
    return isWithinInterval(dayStart, { start: selStart, end: selEnd });
  };
  
  const isRangeStart = (date) => rangeStart && isSameDay(date, rangeStart);
  const isRangeEnd = (date) => rangeEnd && isSameDay(date, rangeEnd);
  
  const handleConfirm = () => {
    if (rangeStart) {
      onDateSelect?.({
        start: rangeStart,
        end: rangeEnd || rangeStart,
        mode: selectionMode,
        formatted: rangeEnd 
          ? `${format(rangeStart, 'd MMM', { locale: it })} - ${format(rangeEnd, 'd MMM yyyy', { locale: it })}`
          : format(rangeStart, 'd MMMM yyyy', { locale: it })
      });
      onClose?.();
    }
  };
  
  const handleFlexible = () => {
    onDateSelect?.({
      start: null,
      end: null,
      mode: 'flexible',
      formatted: 'Date flessibili'
    });
    onClose?.();
  };
  
  if (!isOpen) return null;
  
  const days = generateCalendarDays();
  const weekDays = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
  
  return (
    <div 
      className="fixed inset-y-0 right-0 w-full sm:w-96 bg-white shadow-2xl z-50 flex flex-col animate-slide-in-right"
      data-testid="calendar-drawer"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-umbria-stone/30">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-umbria-olive" />
          <h3 className="font-serif text-lg font-semibold text-umbria-charcoal">
            Quando vuoi venire?
          </h3>
        </div>
        <button 
          onClick={onClose}
          className="p-2 hover:bg-umbria-stone/20 rounded-full transition-colors"
          data-testid="calendar-close-btn"
        >
          <X size={20} />
        </button>
      </div>
      
      {/* Selection mode tabs */}
      <div className="flex gap-1 p-3 border-b border-umbria-stone/20">
        {[
          { mode: 'range', label: 'Periodo' },
          { mode: 'single', label: 'Giorno' },
          { mode: 'flexible', label: 'Flessibile' }
        ].map(({ mode, label }) => (
          <button
            key={mode}
            onClick={() => setSelectionMode(mode)}
            className={`flex-1 py-2 text-sm rounded-lg transition-colors ${
              selectionMode === mode 
                ? 'bg-umbria-olive text-white' 
                : 'bg-umbria-stone/10 text-umbria-charcoal hover:bg-umbria-stone/20'
            }`}
            data-testid={`mode-${mode}`}
          >
            {label}
          </button>
        ))}
      </div>
      
      {/* Calendar */}
      <div className="flex-1 overflow-y-auto p-4">
        {selectionMode === 'flexible' ? (
          <div className="text-center py-8">
            <p className="text-gray-600 mb-4">
              Non hai date precise? Nessun problema!
              <br />
              Chiara ti consiglierà in base alla stagione.
            </p>
            <button
              onClick={handleFlexible}
              className="px-6 py-3 bg-umbria-olive text-white rounded-xl font-medium hover:bg-umbria-olive/90 transition-colors"
              data-testid="flexible-confirm"
            >
              Prosegui senza date
            </button>
          </div>
        ) : (
          <>
            {/* Month navigation */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                className="p-2 hover:bg-umbria-stone/20 rounded-full"
              >
                <ChevronLeft size={20} />
              </button>
              <span className="font-medium text-umbria-charcoal">
                {format(currentMonth, 'MMMM yyyy', { locale: it })}
              </span>
              <button
                onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                className="p-2 hover:bg-umbria-stone/20 rounded-full"
              >
                <ChevronRight size={20} />
              </button>
            </div>
            
            {/* Weekday headers */}
            <div className="grid grid-cols-7 mb-2">
              {weekDays.map(day => (
                <div key={day} className="text-center text-xs text-gray-500 font-medium py-2">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Days grid */}
            <div className="grid grid-cols-7 gap-1">
              {days.map(({ date, isCurrentMonth }, idx) => {
                const selected = isDateSelected(date);
                const isStart = isRangeStart(date);
                const isEnd = isRangeEnd(date);
                const isToday = isSameDay(date, new Date());
                const isPast = date < startOfDay(new Date());
                
                return (
                  <button
                    key={idx}
                    onClick={() => !isPast && handleDayClick(date)}
                    disabled={isPast}
                    className={`
                      aspect-square flex items-center justify-center text-sm rounded-lg transition-all
                      ${!isCurrentMonth ? 'text-gray-300' : ''}
                      ${isPast ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-umbria-olive/10'}
                      ${isToday && !selected ? 'ring-1 ring-umbria-olive' : ''}
                      ${selected ? 'bg-umbria-olive/20' : ''}
                      ${isStart || isEnd ? 'bg-umbria-olive text-white' : ''}
                      ${isStart && rangeEnd ? 'rounded-r-none' : ''}
                      ${isEnd && rangeStart ? 'rounded-l-none' : ''}
                    `}
                    data-testid={`day-${format(date, 'yyyy-MM-dd')}`}
                  >
                    {date.getDate()}
                  </button>
                );
              })}
            </div>
            
            {/* Selection summary */}
            {rangeStart && (
              <div className="mt-4 p-3 bg-umbria-olive/10 rounded-xl">
                <p className="text-sm text-umbria-charcoal">
                  {rangeEnd ? (
                    <>
                      <strong>Dal</strong> {format(rangeStart, 'd MMMM', { locale: it })}
                      {' '}<strong>al</strong> {format(rangeEnd, 'd MMMM yyyy', { locale: it })}
                    </>
                  ) : (
                    <>
                      <strong>Data:</strong> {format(rangeStart, 'd MMMM yyyy', { locale: it })}
                      {selectionMode === 'range' && (
                        <span className="text-gray-500 ml-2">(seleziona la fine)</span>
                      )}
                    </>
                  )}
                </p>
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Footer with confirm */}
      {selectionMode !== 'flexible' && (
        <div className="p-4 border-t border-umbria-stone/30">
          <button
            onClick={handleConfirm}
            disabled={!rangeStart}
            className={`
              w-full py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors
              ${rangeStart 
                ? 'bg-umbria-olive text-white hover:bg-umbria-olive/90' 
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }
            `}
            data-testid="calendar-confirm-btn"
          >
            <Check size={18} />
            Conferma date
          </button>
        </div>
      )}
    </div>
  );
};

export default CalendarDrawer;
